/* eslint-disable max-lines */
import {
  ConfirmationPrompt,
  Group,
  GroupMember,
  IInvitation,
  usePagination,
  useABAC,
  EntitlementsFeatureTooltip,
  JupiterTheme,
  useSnackbarNotification,
  useGroupClient,
} from '@jupiterone/jupiter-web-toolkit';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tab, { TabProps } from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { Send, Shield, User, Users } from 'react-feather';
import { PolicyRule } from 'navbar/src/jupiterAccountSettings/types';
import EditHeader from 'navbar/src/jupiterAccountSettings/AccountSettings/components/EditHeader';
import AddUserTab from './AddUserTab';
import AppPermissions from './AppPermissions';
import InvitationsTab from './InvitationsTab';
import MembersTab from './MembersTab';
import QueryPermissions from './QueryPermissions';
import { createStyles, makeStyles } from '@material-ui/styles';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import useGroups from 'navbar/src/jupiterAccountSettings/hooks/useGroups';
import usePendingSentInvitations from 'navbar/src/jupiterAccountSettings/hooks/usePendingSentInvitations';
import useRelevantPolicies from 'navbar/src/jupiterAccountSettings/hooks/useRelevantPolicies';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    tabLookalike: {
      flex: 1,
      display: 'flex',
      cursor: 'pointer',
    },
  })
);

export interface GroupDetailProps {
  classes: any;
  group: Group;
  tab: number;
  handleBackButton: VoidFunction;
  handleTabChange: (index: number) => void;
}

interface DeletingMemberState {
  deleting: boolean;
  userId: string | null;
  groupId: string | null;
}
interface CloneProps extends Omit<TabProps, 'children'> {
  children: (tabProps: TabProps) => JSX.Element;
}

const initialDeletingMemberState: DeletingMemberState = {
  deleting: false,
  userId: null,
  groupId: null,
};

const GroupDetail = (props: GroupDetailProps) => {
  const { isAdmin } = useABAC();
  const { getGroupPolicyRule, togglePolicyRule } = useRelevantPolicies();
  const {
    invitations,
    deletedInvitation,
    addInvitation,
  } = usePendingSentInvitations();
  const sentInvitations = useMemo(() => {
    return invitations.filter(({ group }) => group === props.group.id);
  }, [props.group.id, invitations]);
  const { updateGroupLocally, saveGroup, deleteGroup } = useGroups();
  const groupClient = useGroupClient();
  const classes = useStyles();
  const notification = useSnackbarNotification();
  const [
    deletingMemberState,
    setDeletingMemberState,
  ] = useState<DeletingMemberState>(initialDeletingMemberState);
  const apiKeysEnabledForGroup = getGroupPolicyRule(
    props.group.id,
    PolicyRule.APIKey
  );
  const {
    data: members,
    setData: setMembers,
    loading,
    fetchMore,
    shouldFetchMore,
  } = usePagination<GroupMember>((pageCursor) => {
    return groupClient
      .getGroupMembers(props.group.id, pageCursor)
      .then(({ links, items }) => ({
        data: items,
        nextPageCursor: links.next,
      }))
      .catch((err) => {
        notification.error({
          message: 'Failed to get group members',
          error: err,
        });
        return {
          data: [],
          nextPageCursor: null,
        };
      });
  });

  const handleDeleteGroup = async () => {
    await deleteGroup(props.group.id);
    props.handleBackButton();
  };

  const CloneProps = (props: CloneProps) => {
    const { children, ...other } = props;
    return children(other);
  };
  const handleTabChange = (_event: any, index: any) => {
    props.handleTabChange(index);
  };
  const updateInvitations = async (
    invitation: IInvitation,
    action: 'remove' | 'add'
  ) => {
    switch (action) {
      case 'remove':
        deletedInvitation(invitation);
        break;
      case 'add':
        await addInvitation(invitation);
        break;
    }
  };

  const handleNameChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    await updateGroupLocally({
      ...props.group,
      name: e.target.value,
    });
  };

  const handleDescriptionChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await updateGroupLocally({
      ...props.group,
      description: e.target.value,
    });
  };

  const submitGroupChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    await saveGroup(props.group);
  };

  const handleApiKeyPolicyChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await togglePolicyRule(props.group.id, PolicyRule.APIKey, e.target.checked);
  };

  const requestRemoveMember = async (userId: string, groupId: string) => {
    setDeletingMemberState({
      deleting: true,
      userId,
      groupId,
    });
  };

  const handleRemoveMember = async () => {
    if (deletingMemberState.userId && deletingMemberState.groupId) {
      setMembers((ms) => ms.filter((m) => m.id !== deletingMemberState.userId));
      try {
        await groupClient.deleteGroupMember(
          deletingMemberState.userId,
          deletingMemberState.groupId
        );
      } catch (_err) {
        notification.error({
          message: 'Failed to remove group member',
          error: _err,
        });
        return [];
      }
    } else {
      throw new Error(
        'Cannot delete a member that does not have a userId and groupId'
      );
    }
  };

  return (
    <>
      <EditHeader
        handleBackButton={props.handleBackButton}
        titleText={props.group.name}
        titleIcon={<Users />}
        onDeleteItem={handleDeleteGroup}
      >
        <>
          <form data-testid="group-detail" onSubmit={(e) => e.preventDefault()}>
            <TextField
              id="group-name"
              label="Group name"
              value={props.group.name}
              className={props.classes.textField}
              InputLabelProps={{
                className: props.classes.textInputLabel,
                classes: {
                  focused: props.classes.textInputLabelFocused,
                },
              }}
              InputProps={{
                className: props.classes.textInput,
              }}
              margin="normal"
              fullWidth={true}
              onChange={handleNameChange}
              onBlur={submitGroupChange}
            />
            <TextField
              id="group-desc"
              label="Group description"
              value={props.group.description}
              className={props.classes.textField}
              InputLabelProps={{
                className: props.classes.textInputLabel,
                classes: {
                  focused: props.classes.textInputLabelFocused,
                },
              }}
              InputProps={{
                className: props.classes.textInput,
              }}
              margin="normal"
              fullWidth={true}
              onChange={handleDescriptionChange}
              onBlur={submitGroupChange}
            />
            {isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={apiKeysEnabledForGroup}
                    onChange={handleApiKeyPolicyChange}
                    value="apiKeysEnabled"
                  />
                }
                label="Enable API key access for users in this group"
                classes={{
                  root: props.classes.checkbox,
                  label: props.classes.textInput,
                }}
              />
            )}
          </form>
          <Tabs
            className={props.classes.tabs}
            value={props.tab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
          >
            <Tab
              classes={{
                textColorInherit: props.classes.tab,
                root: props.classes.tabText,
              }}
              icon={
                <Tooltip enterDelay={500} placement="top" title="Members">
                  <span>
                    <User />
                  </span>
                </Tooltip>
              }
            />
            <Tab
              classes={{
                textColorInherit: props.classes.tab,
                root: props.classes.tabText,
              }}
              data-testid="send-tab-icon"
              icon={
                <Tooltip
                  enterDelay={500}
                  placement="top"
                  title="Sent Invitations"
                >
                  <span>
                    <Send />
                  </span>
                </Tooltip>
              }
            />
            <CloneProps>
              {(tabProps: TabProps) => (
                <EntitlementsFeatureTooltip
                  fallbackTitle="Access Control"
                  entitlement={
                    FeatureEntitlementName.RBAC_AND_QUERY_PERMISSIONS
                  }
                  enterDelay={500}
                  placement="top"
                  childrenContainerClass={classes.tabLookalike}
                >
                  {(hasEntitlement) => (
                    <Tab
                      {...tabProps}
                      className={classes.tabLookalike}
                      disabled={!hasEntitlement}
                      classes={{
                        textColorInherit: props.classes.tab,
                        root: props.classes.tabText,
                      }}
                      data-testid="add-groups-tab"
                      icon={
                        <span data-testid="shield-icon">
                          <Shield />
                        </span>
                      }
                    />
                  )}
                </EntitlementsFeatureTooltip>
              )}
            </CloneProps>
          </Tabs>
          {props.tab === 0 && (
            <MembersTab
              classes={props.classes}
              group={props.group}
              onRemoveUser={requestRemoveMember}
              members={members}
              loading={loading}
              loadMore={fetchMore}
              finishedLoadingData={!shouldFetchMore}
            />
          )}
          {props.tab === 1 && (
            <div
              data-testid="second-tab"
              className={props.classes.tabContainer}
            >
              <AddUserTab
                classes={props.classes}
                onUpdateInvitations={updateInvitations}
                group={props.group}
              />

              {sentInvitations.length > 0 && (
                <InvitationsTab
                  classes={props.classes}
                  invitations={sentInvitations}
                  onUpdateInvitations={updateInvitations}
                />
              )}
            </div>
          )}
          {props.tab === 2 && (
            <div data-testid="third-tab" className={props.classes.tabContainer}>
              <QueryPermissions groupId={props.group.id} />
              <AppPermissions groupId={props.group.id} />
            </div>
          )}
        </>
      </EditHeader>
      <ConfirmationPrompt
        variant="delete"
        confirmButtonText="Delete"
        open={deletingMemberState.deleting}
        message={`Are you sure you want to delete ${deletingMemberState.userId} from this group?`}
        onClose={() => {
          setDeletingMemberState(initialDeletingMemberState);
        }}
        onConfirm={handleRemoveMember}
      />
    </>
  );
};
export default GroupDetail;
